import React, { useRef, useState, useEffect, useCallback, useContext } from 'react';
import { TextField, Button, Typography, Card, CardContent, Grid } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from "../../../../services/api";
import { CadastroContext } from '../../../../contexts/CadastroContext';
import AlertMessage from '../../../Alert';

const AsoValidaTokenEmail = () => {
    const { cadastro, handleCadastroChange } = useContext(CadastroContext);
    const countdownTime = 120;
    const [time, setTime] = useState(countdownTime);
    const [timerEnded, setTimerEnded] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: "", variant: "success" });
    const [values, setValues] = useState<string[]>(["", "", "", ""]);
    const { dbName } = useParams();
    const location = useLocation();

    const inputRefs: any = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];

    useEffect(() => {
        const timerInterval = setInterval(() => {
            if (time <= 0) {
                clearInterval(timerInterval);
                setTimerEnded(true);
            } else {
                setTime(prevTime => prevTime - 1);
            }
        }, 1000);
        return () => clearInterval(timerInterval);
    }, [time]);

    const handleInput = useCallback((e: React.KeyboardEvent<HTMLInputElement> & React.ChangeEvent<HTMLInputElement>, index: number) => {
        const newValues = [...values];
        const { value } = e.target;
        if (/^\d*$/.test(value)) {  // Allow only digits
            newValues[index] = value;
            setValues(newValues);

            if (e.type === 'change') {
                if (value.length === 1 && index < inputRefs.length - 1) {
                    inputRefs[index + 1]?.current?.focus();
                }
            } else if (e.type === 'keydown') {
                if (e.key === 'Backspace' && value.length === 0 && index > 0) {
                    inputRefs[index - 1]?.current?.focus();
                }
            }
        }
    }, [values, inputRefs]);

    const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedData = e.clipboardData.getData('text');
        if (/^\d+$/.test(pastedData) && pastedData.length === values.length) {
            const newValues = pastedData.split('');
            setValues(newValues);
            newValues.forEach((value, index) => {
                if (inputRefs[index].current) {
                    inputRefs[index].current.value = value;
                }
            });
            inputRefs[values.length - 1]?.current?.focus();
        }
    }, [values.length, inputRefs]);

    const navigate = useNavigate();

    const handleResendCode = useCallback(() => {
        setValues(["", "", "", ""]);
    }, []);

    const handleResetTimer = useCallback(async () => {
        try {
            setTime(countdownTime);
            setTimerEnded(false);
            handleResendCode();
            await api.post('/recebe-disparo-email', { email: cadastro.email });
            setAlert({ show: true, message: "Código Reenviado.", variant: "success" });
        } catch (error) {
            setAlert({ show: true, message: "Email Inválido.", variant: "danger" });
        }
    }, [cadastro.email, countdownTime]);

    const formatTime = useCallback((time: number) => `${Math.floor(time / 60).toString().padStart(2, '0')}:${(time % 60).toString().padStart(2, '0')}`, []);

    const handleSubmit = async () => {
        const token = values.join('');
        try {
            const response = await api.post('/recebe-token-validacao', { token });
            console.log("🚀 ~ handleSubmit ~ response.data.message:", response.data.message)
            if (response.data.message === 'Token válido') {
                cadastro.emailValidado = true;
                handleCadastroChange(cadastro);
                if (cadastro.isEmpresa && !cadastro.reaproveitamentoAso) {
                    return navigate(`/aso/registro/validar-info-empresa/${cadastro.idConsulta}`);
                } else if (cadastro.reaproveitamentoAso) {
                    return navigate('/aso/registro/validar-info-empresa');
                } else {
                    if (location.pathname === `/aso/registro/validar-email-token-aso/${dbName}`) {
                        navigate(`/aso/registro/validar-info-aso/${dbName}`);
                    } else {
                        navigate(`/aso/registro/validar-info/${cadastro.idConsulta}/${dbName}`);
                    }
                }
            } else {
                setAlert({ show: true, message: "Erro ao validar, por favor reenvie o código!", variant: "danger" });
            }
        } catch (error) {
            setAlert({ show: true, message: "Token Inválido ou expirado.", variant: "danger" });
        }
    };

    return (
        <>
            <Card sx={{ maxWidth: 400, mx: 'auto', mt: 5, p: 2, backgroundColor: 'white', borderRadius: '6px' }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom textAlign={'center'} style={{ fontWeight: '600' }}>Digite o código que enviamos por e-mail.</Typography>
                    <Typography marginBottom={'10px'} padding={'5px'} textAlign={'center'}>Ele foi enviado para <strong>{cadastro.email}</strong>. Se não o encontrar, confira sua caixa de spam ou <a href="#">altere seu e-mail</a>.</Typography>
                    <Grid container spacing={2} justifyContent="center">
                        {values.map((value, index) => (
                            <Grid item key={index}>
                                <TextField
                                    type="text"
                                    inputRef={inputRefs[index]}
                                    onChange={(e: any) => handleInput(e, index)}
                                    onKeyDown={(e: any) => handleInput(e, index)}
                                    onPaste={index === 0 ? handlePaste : undefined}  // Add onPaste to the first input
                                    value={value}
                                    inputProps={{ maxLength: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                                    variant="outlined"
                                    sx={{ width: '50px', textAlign: 'center' }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    <Button fullWidth variant="contained" onClick={handleSubmit} sx={{ mt: 2 }}>
                        Confirmar código
                    </Button>
                    {timerEnded ? (
                        <Button fullWidth variant="outlined" onClick={handleResetTimer} sx={{ mt: 1 }}>
                            Reenviar Código
                        </Button>
                    ) : (
                        <Button fullWidth variant="outlined" disabled sx={{ mt: 1 }}>
                            Reenviar Código {formatTime(time)}
                        </Button>
                    )}
                </CardContent>
            </Card>
            <AlertMessage
                show={alert.show}
                message={alert.message}
                variant={alert.variant}
                onClose={() => setAlert({ ...alert, show: false })}
            />
        </>
    );
}

export default AsoValidaTokenEmail;